import {
  useCallback,
  useState,
  useLayoutEffect,
  type DependencyList,
} from "react";

/**
 * Reports whether an element's content is clipped, and keeps reporting it.
 *
 * `deps`: re-measure when the rendered text changes.
 *
 * The ref is a callback ref and the node is a dependency, so the observer
 * reattaches when a caller swaps the element (rendering it bare while it fits
 * and wrapped once it does not remounts the node React observes).
 */
export function useTextOverflow<T extends HTMLElement = HTMLElement>(
  deps: DependencyList = []
) {
  const [node, setNode] = useState<T | null>(null);
  const [isOverflowing, setIsOverflowing] = useState(false);

  const ref = useCallback((element: T | null) => setNode(element), []);

  useLayoutEffect(() => {
    if (!node) return undefined;

    const checkOverflow = () => {
      setIsOverflowing(
        node.scrollWidth > node.clientWidth ||
          node.scrollHeight > node.clientHeight
      );
    };
    checkOverflow();

    const observer = new ResizeObserver(checkOverflow);
    observer.observe(node);
    return () => observer.disconnect();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [node, ...deps]);

  return { ref, isOverflowing };
}
